// Render INLINE markdown-lite của bài Kênh Cộng Đồng để admin XEM TRƯỚC đúng như
// web/app hiển thị: **đậm**, *nghiêng*, ~~gạch ngang~~, [chữ](link), link tự
// nhận diện, #MÃGIẢMGIÁ thành chip vàng. Cấu trúc khối (tiêu đề/gạch đầu
// dòng/ảnh chèn) ở ./community-content-blocks.js.
//
// Regex & thứ tự parse copy đúng từ frontend/src/pages/Community/community-linkified-text.js
// (admin là CRA riêng nên không import chéo được). Sửa rule ở đây PHẢI sửa cả bên
// frontend + mobile/src/modules/community-channel/utils/community-format.ts.
import React from "react";

const URL_SPLIT_REGEX = /((?:https?:\/\/|www\.)[^\s]+)/gi;
const URL_TEST_REGEX = /^(?:https?:\/\/|www\.)/i;
const TRAILING_PUNCT_REGEX = /[.,!?)\]]+$/;

// Bold parse TRƯỚC italic để **đậm** không bị hiểu thành 2 lượt *nghiêng* lồng nhau.
// [^*] (không chặn \n) → bôi đen nhiều dòng rồi bấm B vẫn in đậm, khớp web + app.
// Đậm nới lỏng: chỉ cần có 1 ký tự non-space bên trong (`** MINIGAME 07**` vẫn đậm);
// nghiêng giữ chặt để "3 * 4 * 5" không thành chữ nghiêng.
const BOLD_SPLIT_REGEX = /(\*\*[^*]*\S[^*]*\*\*)/g;
const BOLD_FULL_REGEX = /^\*\*([^*]*\S[^*]*)\*\*$/;
const ITALIC_SPLIT_REGEX = /(\*\S(?:[^*]*\S)?\*)/g;
const ITALIC_FULL_REGEX = /^\*(\S(?:[^*]*\S)?)\*$/;
// ~~gạch ngang~~ (không chứa dấu * nên tách trước bold/italic)
const STRIKE_SPLIT_REGEX = /(~~[^~]*\S[^~]*~~)/g;
const STRIKE_FULL_REGEX = /^~~([^~]*\S[^~]*)~~$/;
// [Đăng ký ngay](https://...) — tách TRƯỚC link tự nhận diện
const LABELED_LINK_SPLIT_REGEX = /(\[[^\]\n]+\]\((?:https?:\/\/|www\.)[^\s)]+\))/g;
const LABELED_LINK_FULL_REGEX = /^\[([^\]\n]+)\]\(((?:https?:\/\/|www\.)[^\s)]+)\)$/;

// #MÃ in hoa 4-20 ký tự, không dính chữ/số phía sau (không dùng lookbehind — Safari cũ crash).
const INLINE_CODE_SPLIT_REGEX = /(#[A-Z0-9]{4,20}(?![A-Za-z0-9]))/g;
const INLINE_CODE_FULL_REGEX = /^#([A-Z0-9]{4,20})$/;

/** Tách ~~gạch ngang~~ rồi tới bold/italic. */
function renderStrike(text, keyPrefix, linkClass, onBg) {
    if (!text) return null;
    return text.split(STRIKE_SPLIT_REGEX).map((part, sIdx) => {
        if (!part) return null;
        const key = `${keyPrefix}-s${sIdx}`;
        const strikeMatch = part.match(STRIKE_FULL_REGEX);
        if (strikeMatch) {
            return (
                <span key={key} className="line-through">
                    {renderBoldItalic(strikeMatch[1], key, linkClass, onBg)}
                </span>
            );
        }
        return (
            <React.Fragment key={key}>
                {renderBoldItalic(part, key, linkClass, onBg)}
            </React.Fragment>
        );
    });
}

/**
 * Tách bold rồi italic, sau đó mới xuống lá (chip mã + link trần).
 *
 * Thứ tự bắt buộc: bóc style TRƯỚC, tìm link SAU. Làm ngược lại thì
 * `**https://a.vn**` bị cắt thành `**` + link + `**`, hai dấu ** rơi vào hai đoạn
 * khác nhau nên không ghép được thành cặp -> dấu ** hiện nguyên ra màn hình.
 */
function renderBoldItalic(text, keyPrefix, linkClass, onBg) {
    if (!text) return null;
    return text.split(BOLD_SPLIT_REGEX).map((boldPart, bIdx) => {
        if (!boldPart) return null;
        const boldMatch = boldPart.match(BOLD_FULL_REGEX);
        if (boldMatch) {
            const key = `${keyPrefix}-b${bIdx}`;
            return <strong key={key}>{renderLeaf(boldMatch[1], key, linkClass, onBg)}</strong>;
        }
        return boldPart.split(ITALIC_SPLIT_REGEX).map((italicPart, iIdx) => {
            if (!italicPart) return null;
            const italicMatch = italicPart.match(ITALIC_FULL_REGEX);
            const key = `${keyPrefix}-b${bIdx}-i${iIdx}`;
            if (italicMatch) return <em key={key}>{renderLeaf(italicMatch[1], key, linkClass, onBg)}</em>;
            return (
                <React.Fragment key={key}>
                    {renderLeaf(italicPart, key, linkClass, onBg)}
                </React.Fragment>
            );
        });
    });
}

/** LÁ: chip mã #MÃ + link trần. Style đã được bóc ở tầng trên. */
function renderLeaf(text, keyPrefix, linkClass, onBg) {
    if (!text) return null;
    return text.split(INLINE_CODE_SPLIT_REGEX).map((codePart, cIdx) => {
        if (!codePart) return null;
        const key = `${keyPrefix}-c${cIdx}`;
        const codeMatch = codePart.match(INLINE_CODE_FULL_REGEX);
        if (codeMatch) {
            return (
                <span
                    key={key}
                    className={`inline-block rounded px-1.5 py-0.5 mx-0.5 text-xs font-bold ${
                        onBg ? "bg-surface/90 text-content" : "bg-yellow-100 text-yellow-800 border border-yellow-300"
                    }`}
                >
                    {codeMatch[1]}
                </span>
            );
        }
        return <React.Fragment key={key}>{renderBareLinked(codePart, linkClass, onBg)}</React.Fragment>;
    });
}

/**
 * Text thuần -> mảng node React (link bọc <a>, phần còn lại parse markdown-lite).
 * @param {string} text
 * @param {boolean} onBg true khi hiển thị trên nền gradient (đổi màu link/chip cho dễ đọc)
 */
export function renderCommunityRichText(text, onBg = false) {
    if (!text) return null;
    const linkClass = onBg ? "underline text-white break-all" : "font-semibold text-blue-700 break-all";
    // Link có chữ hiển thị tách trước, phần còn lại mới dò link trần
    return String(text)
        .split(LABELED_LINK_SPLIT_REGEX)
        .map((chunk, cIdx) => {
            if (!chunk) return null;
            const labeled = chunk.match(LABELED_LINK_FULL_REGEX);
            if (!labeled) {
                return (
                    <React.Fragment key={`l${cIdx}`}>
                        {renderStrike(chunk, `l${cIdx}`, linkClass, onBg)}
                    </React.Fragment>
                );
            }
            const url = labeled[2];
            return (
                <a
                    key={`l${cIdx}`}
                    href={url.startsWith("www.") ? `https://${url}` : url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkClass}
                >
                    {labeled[1]}
                </a>
            );
        });
}

/** Dò link trần (http/www) trong đoạn đã loại link có chữ hiển thị. */
function renderBareLinked(text, linkClass, onBg) {
    return String(text)
        .split(URL_SPLIT_REGEX)
        .map((part, idx) => {
            if (!part) return null;
            if (!URL_TEST_REGEX.test(part)) {
                return <React.Fragment key={idx}>{part}</React.Fragment>;
            }
            const trailingMatch = part.match(TRAILING_PUNCT_REGEX);
            const trailing = trailingMatch ? trailingMatch[0] : "";
            const cleanUrl = trailing ? part.slice(0, -trailing.length) : part;
            const href = cleanUrl.startsWith("www.") ? `https://${cleanUrl}` : cleanUrl;
            return (
                <React.Fragment key={idx}>
                    <a href={href} target="_blank" rel="noopener noreferrer" className={linkClass}>
                        {cleanUrl}
                    </a>
                    {trailing}
                </React.Fragment>
            );
        });
}
